import { Box, Typography } from '@mui/material';

import { BlockTitle, FieldGrid, ObjectTable } from './ReportViewer';

const MUTED = '#98A2B3';

// The report's parts this section shows; the Full report leaves them out as sections of their own.
export const NETWORK_REPORT_KEYS = ['network', 'wifi', 'bluetooth'];

const macOf = (value) => (value ? String(value).toLowerCase() : null);
const yesNo = (value) => (value === true ? 'Yes' : value === false ? 'No' : null);
const joinParts = (parts, separator = ' ') => parts.filter((part) => part !== null && part !== undefined && part !== '').join(separator) || null;
const radioState = (radio) => (radio?.present === false ? 'Not present' : radio?.present !== true ? 'Unknown'
    : radio.enabled === true ? 'On' : radio.enabled === false ? 'Off' : 'Present');

// One network card as a table row. Schema 1.7 interfaces, and the older { name, kind, mac, up, speedMbps }.
const describeInterface = (nic) => {
    const up = nic.link?.up ?? nic.up;
    const speed = nic.link?.negotiatedSpeedMbps ?? nic.speedMbps;
    return {
        name: nic.name,
        type: nic.type ?? (nic.kind ? String(nic.kind).toUpperCase() : null),
        macAddress: macOf(nic.macAddress ?? nic.mac),
        ipv4: nic.ip?.ipv4,
        ipv6: nic.ip?.ipv6,
        gateway: nic.ip?.gateway,
        dhcp: yesNo(nic.ip?.dhcp),
        link: joinParts([up === true ? 'Up' : up === false ? 'Down' : null, speed && `${speed} Mbps`, nic.link?.duplex], ' · '),
        model: joinParts([nic.manufacturer, nic.model]),
        driver: nic.driver?.name ?? (typeof nic.driver === 'string' ? nic.driver : null),
    };
};

// Network cards only: no loopback or virtual interfaces.
const interfacesOf = (hardware) => (Array.isArray(hardware?.network?.interfaces) ? hardware.network.interfaces : [])
    .filter((nic) => nic && nic.physical !== false && String(nic.kind ?? '').toLowerCase() !== 'loopback')
    .map(describeInterface);

const wifiAdaptersOf = (hardware) => (Array.isArray(hardware?.wifi?.devices) ? hardware.wifi.devices : [])
    .map((adapter) => ({ name: adapter.name, model: joinParts([adapter.manufacturer, adapter.model]), macAddress: macOf(adapter.macAddress), driver: adapter.driver, connection: adapter.connection }));

const bluetoothAdaptersOf = (hardware) => (Array.isArray(hardware?.bluetooth?.devices) ? hardware.bluetooth.devices : [])
    .map((adapter) => ({ name: adapter.name, model: joinParts([adapter.manufacturer, adapter.model]), id: joinParts([adapter.vendorId, adapter.productId], ':') }));

const listeningPortsOf = (hardware) => (Array.isArray(hardware?.network?.listeningPorts) ? hardware.network.listeningPorts : [])
    .map((port) => ({ protocol: port.protocol, address: port.address, port: port.port, process: port.processName, user: port.username, commandLine: port.commandLine }));

// Network ports of remote management that work without the OS: Intel AMT and IPMI (BMC).
const outOfBandOf = (hardware) => {
    const amtLan = hardware?.security?.outOfBandManagement?.amt?.lan;
    const ipmiLan = hardware?.ipmi?.lan;
    return [
        ...(Array.isArray(amtLan) ? amtLan : []).map((lan) => ({
            source: 'Intel AMT', interface: lan.interface, enabled: yesNo(lan.enabled), dhcp: yesNo(lan.dhcp), ipv4: lan.ipv4, macAddress: macOf(lan.mac),
        })),
        ...(ipmiLan?.ipAddress || ipmiLan?.macAddress ? [{
            source: 'IPMI', interface: ipmiLan.channel, enabled: yesNo(ipmiLan.configured), dhcp: ipmiLan.ipAddressSource, ipv4: ipmiLan.ipAddress, macAddress: macOf(ipmiLan.macAddress),
        }] : []),
    ];
};

const Block = ({ title, items, empty = 'None' }) => (
    <Box>
        <BlockTitle count={items.length}>{title}</BlockTitle>
        {items.length ? <ObjectTable items={items} /> : <Typography variant='body2' sx={{ color: MUTED }}>{empty}</Typography>}
    </Box>
);

// The device's network as the Full report's Network section: { key, name, count, content }.
// Everything comes from the hardware report; the registered MAC is checked against the MACs it lists.
export const networkReportSection = (device) => {
    const hardware = device?.hardware ?? {};
    const interfaces = interfacesOf(hardware);
    const wifiAdapters = wifiAdaptersOf(hardware);
    const bluetoothAdapters = bluetoothAdaptersOf(hardware);
    const listeningPorts = listeningPortsOf(hardware);
    const outOfBand = outOfBandOf(hardware);

    const registeredMac = macOf(device?.macAddress);
    const reportedMacs = new Set([...interfaces, ...wifiAdapters].map((item) => item.macAddress).filter(Boolean));
    const fields = [
        ['hostname', device?.hostname ?? hardware.hostname],
        ['registeredMacAddress', registeredMac],
        ['primaryMacAddress', macOf(hardware.primaryMac ?? hardware.device?.identity?.fingerprintInputs?.primaryMac)],
        ['registeredMacInReport', !registeredMac || !reportedMacs.size ? null : reportedMacs.has(registeredMac) ? 'Yes' : 'No'],
        ['interfacesUp', interfaces.length ? `${interfaces.filter((nic) => nic.link?.startsWith('Up')).length} of ${interfaces.length}` : null],
        ['wifi', radioState(hardware.wifi)],
        ['bluetooth', radioState(hardware.bluetooth)],
        ['listeningPorts', listeningPorts.length],
    ];

    const content = !Object.keys(hardware).length
        ? <Typography variant='body2' sx={{ color: MUTED }}>No network details: this device has not sent a hardware report.</Typography>
        : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <FieldGrid entries={fields} />
                <Block title='Network interfaces' items={interfaces} />
                <Block title='Wi-Fi adapters' items={wifiAdapters} />
                <Block title='Bluetooth adapters' items={bluetoothAdapters} />
                <Block title='Listening ports' items={listeningPorts} />
                <Block title='Out-of-band management network' items={outOfBand} empty='None reported' />
            </Box>
        );

    return { key: '__network__', name: 'Network', count: interfaces.length + wifiAdapters.length + bluetoothAdapters.length, content };
};
